import { useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Coins, ChatCircleText, ArrowUpRight, ArrowDownRight } from "@phosphor-icons/react";
import { fmtBRL } from "@/lib/format";

const PlayerLedgerCard = ({ players }) => (
    <Card className="card-lux">
        <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
                <Coins size={22} weight="duotone" className="text-gold" />
                <h3 className="font-serif text-xl">Player ledger</h3>
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="player-ledger">
                {players.length === 0 && (
                    <div className="text-sm text-muted-foreground">No transactions yet.</div>
                )}
                {players.map(p => (
                    <div key={p.player_id} className="border border-border rounded-md p-3">
                        <div className="flex justify-between items-center">
                            <span className="text-sm font-medium">{p.player_name}</span>
                            <span className={`text-sm metric-num ${p.net >= 0 ? "text-gold" : "text-destructive"}`}>
                                {p.net >= 0 ? "+" : ""}{fmtBRL(p.net)}
                            </span>
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                            <span>Buy: {fmtBRL(p.buyin)}</span>
                            <span>Cash: {fmtBRL(p.cashout)}</span>
                        </div>
                    </div>
                ))}
            </div>
        </CardContent>
    </Card>
);

const WaitlistCard = ({ entries }) => (
    <Card className="card-lux">
        <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
                <ChatCircleText size={22} weight="duotone" className="text-gold" />
                <h3 className="font-serif text-xl">Waitlist</h3>
            </div>
            <div className="space-y-2" data-testid="dealer-waitlist">
                {entries.length === 0 && (
                    <div className="text-sm text-muted-foreground">No one in queue.</div>
                )}
                {entries.map((w, i) => (
                    <div key={w.id} className="flex justify-between items-center text-sm border-b border-border/40 py-1">
                        <span><span className="text-gold mr-2">#{i + 1}</span>{w.player_name}</span>
                        <span className="text-xs text-muted-foreground font-mono-plex">{w.player_phone}</span>
                    </div>
                ))}
            </div>
        </CardContent>
    </Card>
);

const TransactionRow = ({ tx }) => (
    <div className="flex items-center justify-between border-b border-border/40 py-1">
        <span className="flex items-center gap-2">
            {tx.kind === "buy"
                ? <ArrowDownRight size={14} className="text-gold" />
                : <ArrowUpRight size={14} className="text-destructive" />}
            {tx.player_name}
            {tx.kind === "buy" && tx.method && tx.method !== "cash" && (
                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-gold/40 text-gold ml-1">
                    {tx.method}
                </span>
            )}
        </span>
        <span className="metric-num text-sm">{fmtBRL(tx.amount)}</span>
    </div>
);

const TransactionsCard = ({ transactions }) => (
    <Card className="card-lux">
        <CardContent className="p-5">
            <h3 className="font-serif text-xl mb-3">Recent transactions</h3>
            <div className="space-y-1.5 max-h-60 overflow-y-auto text-sm">
                {transactions.length === 0 && (
                    <div className="text-muted-foreground text-sm">No transactions.</div>
                )}
                {transactions.map(x => <TransactionRow key={x.id} tx={x} />)}
            </div>
        </CardContent>
    </Card>
);

export default function DealerSidebar({ data }) {
    const activeWaitlist = useMemo(
        () => (data.waitlist || []).filter(w => !w.notified),
        [data.waitlist]
    );

    const recentTx = useMemo(() => {
        const buys = (data.buyins || []).map(b => ({ ...b, kind: "buy" }));
        const cashes = (data.cashouts || []).map(c => ({ ...c, kind: "cash" }));
        return [...buys, ...cashes]
            .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
            .slice(0, 12);
    }, [data.buyins, data.cashouts]);

    return (
        <div className="space-y-6">
            <PlayerLedgerCard players={data.players} />
            <WaitlistCard entries={activeWaitlist} />
            <TransactionsCard transactions={recentTx} />
        </div>
    );
}
